import { create } from 'zustand';
import { ITask } from '../@shared/interfaces/task.interfaces';

interface CalendarUIState {
  // UI State only
  selectedTask: ITask | null;
  showTaskOverlay: boolean;

  // Date range for UI synchronization (not data fetching)
  dateRange: {
    startDate: Date;
    endDate: Date;
  };

  // UI Actions
  selectTask: (taskId: string | null, allTasks?: ITask[]) => void;
  createNewTask: (taskData?: Partial<ITask>) => void;
  setShowTaskOverlay: (show: boolean) => void;
  setDateRange: (startDate: Date, endDate: Date) => void;
  setDateRangeFromDays: (numberOfDays: number) => void;
  handleTaskAction: (action: {
    type: string;
    task?: ITask;
    taskId?: string;
  }) => void;
}

export const useCalendarUIStore = create<CalendarUIState>((set) => ({
  // Initial UI state
  selectedTask: null,
  showTaskOverlay: false,
  dateRange: {
    startDate: (() => {
      const date = new Date();
      date.setHours(0, 0, 0, 0);
      return date;
    })(),
    endDate: (() => {
      const date = new Date();
      date.setDate(date.getDate() + 3);
      date.setHours(23, 59, 59, 999);
      return date;
    })(),
  },

  // Set date range for UI synchronization
  setDateRange: (startDate: Date, endDate: Date) => {
    set({ dateRange: { startDate, endDate } });
  },

  // Set date range based on number of days from today
  setDateRangeFromDays: (numberOfDays: number) => {
    // Start from today at 00:00:00
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);

    // End at numberOfDays from now at 23:59:59
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + numberOfDays);
    endDate.setHours(23, 59, 59, 999);

    set({ dateRange: { startDate, endDate } });
  },

  // Select a task to view/edit
  selectTask: (taskId: string | null, allTasks?: ITask[]) => {
    if (!taskId) {
      set({ selectedTask: null });
      return;
    }

    if (allTasks) {
      const task = allTasks.find((t) => t._id === taskId);
      if (task) {
        set({ selectedTask: task });
      }
    }
  },

  // Set task overlay visibility
  setShowTaskOverlay: (show: boolean) => {
    set({ showTaskOverlay: show });
    // If hiding overlay, clear selected task
    if (!show) {
      set({ selectedTask: null });
    }
  },

  // Handle task actions (save, delete, cancel, close)
  handleTaskAction: (action) => {
    if (
      action.type === 'close' ||
      action.type === 'cancel' ||
      action.type === 'deleted'
    ) {
      set({ showTaskOverlay: false, selectedTask: null });
    } else if (action.type === 'saved') {
      set({ showTaskOverlay: false, selectedTask: null });
      // React Query will handle refetching automatically through query invalidation
    }
  },

  // Create a new task with predefined data
  createNewTask: (taskData?: Partial<ITask>) => {
    set({ selectedTask: { ...taskData } as ITask });
  },
}));
